import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { whatsappLink } from "@/lib/clinic";

const dermServices = [
    "Skin Analysis",
    "Hair Analysis",
    "Acne & Pores Treatment",
    "Hair & Skin PRP",
    "Skin Pigmentation Treatment",
    "Venereology Consultation",
];

const dentalServices = [
    "Endodontic (Root Canal) Work",
    "Cosmetic Dentistry",
    "Smile Makeovers",
    "Teeth Whitening",
    "Restorations & Fillings",
    "General Dental Care",
];

export default function Services() {
    return (
        <section
            id="services"
            className="py-24 md:py-32 bg-[#F3F1EC]/60 relative"
            data-testid="services-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <span className="text-xs uppercase tracking-[0.3em] text-[#8A9A5B] font-medium">
                        — Our Services
                    </span>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#1A221F] mt-4 leading-[1.1] tracking-tight">
                        Two specialties. One <em className="italic text-[#3B5249]">calm</em> roof.
                    </h2>
                    <p className="text-base md:text-lg text-[#4A5D54] leading-relaxed mt-6">
                        From advanced skin and hair therapies to precision endodontic work
                        and cosmetic dentistry — each treatment is led by a senior
                        consultant.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mt-16">
                    {/* Dermatology */}
                    <motion.article
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="group relative overflow-hidden rounded-[2rem] bg-white border border-[#E8E6E1] shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] transition-all duration-500"
                        data-testid="service-dermatology-card"
                    >
                        <div className="aspect-[16/9] overflow-hidden">
                            <img
                                src="/images/service-dermatology.jpg"
                                alt="Dermatology laser treatment room"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                            />
                        </div>
                        <div className="p-8 md:p-10">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="text-xs uppercase tracking-[0.25em] text-[#8A9A5B]">
                                        Dermatology
                                    </span>
                                    <h3 className="font-display text-2xl md:text-3xl text-[#1A221F] font-medium mt-2">
                                        Skin, Hair & Venereology
                                    </h3>
                                    <p className="text-sm text-[#7A8C84] mt-1">
                                        Led by Dr. Hitesh Khatri · DVL
                                    </p>
                                </div>
                                <a
                                    href={whatsappLink("I'd like to consult for a dermatology concern.")}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-12 h-12 rounded-full bg-[#3B5249] text-white flex items-center justify-center shrink-0 hover:scale-110 transition-transform"
                                    aria-label="Book dermatology consultation"
                                    data-testid="service-dermatology-book"
                                >
                                    <ArrowUpRight className="w-5 h-5" />
                                </a>
                            </div>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-8">
                                {dermServices.map((s) => (
                                    <li
                                        key={s}
                                        className="flex items-center gap-2 text-sm text-[#4A5D54]"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#8A9A5B]" />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.article>

                    {/* Dental */}
                    <motion.article
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="group relative overflow-hidden rounded-[2rem] bg-white border border-[#E8E6E1] shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] transition-all duration-500"
                        data-testid="service-dental-card"
                    >
                        <div className="aspect-[16/9] overflow-hidden">
                            <img
                                src="/images/service-dental.jpg"
                                alt="Dental treatment room"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                            />
                        </div>
                        <div className="p-8 md:p-10">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="text-xs uppercase tracking-[0.25em] text-[#8A9A5B]">
                                        Dental
                                    </span>
                                    <h3 className="font-display text-2xl md:text-3xl text-[#1A221F] font-medium mt-2">
                                        Endodontics & Cosmetic
                                    </h3>
                                    <p className="text-sm text-[#7A8C84] mt-1">
                                        Led by Dr. Chitra Sachdev · Endodontist
                                    </p>
                                </div>
                                <a
                                    href={whatsappLink("I'd like to consult for a dental concern.")}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-12 h-12 rounded-full bg-[#3B5249] text-white flex items-center justify-center shrink-0 hover:scale-110 transition-transform"
                                    aria-label="Book dental consultation"
                                    data-testid="service-dental-book"
                                >
                                    <ArrowUpRight className="w-5 h-5" />
                                </a>
                            </div>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-8">
                                {dentalServices.map((s) => (
                                    <li
                                        key={s}
                                        className="flex items-center gap-2 text-sm text-[#4A5D54]"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#5E7C88]" />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.article>
                </div>
            </div>
        </section>
    );
}
